import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useCompanySettings } from "../lib/useCompanySettings";
import { whatsappHref } from "../lib/phone";
import { Button } from "./ui/button";
import { Phone, MessageCircle, Mail, MapPin, Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";

export function WhatsAppFloating() {
  const s = useCompanySettings();
  const href = whatsappHref(s.whatsapp);
  if (!href) return null;
  return (
    <a
      data-testid="whatsapp-float-btn"
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`WhatsApp ${s.whatsapp}`}
      className="fixed bottom-6 right-6 z-[9999] bg-[#25D366] hover:bg-[#1ebd5b] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();

  const link = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-[#D4AF37]" : "text-white/80 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/10">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-baseline gap-2">
          <span className="font-heading text-2xl tracking-tight text-white">Sam</span>
          <span className="font-heading text-2xl tracking-tight text-gold-gradient">Taxi</span>
          <span className="hidden sm:inline text-[10px] tracking-[0.3em] text-white/40 uppercase ml-2">NL</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" className={link} data-testid="nav-home" end>{t("nav.home")}</NavLink>
          <NavLink to="/airports/schiphol" className={link} data-testid="nav-airports">{t("nav.airports")}</NavLink>
          <NavLink to="/#book" className={link} data-testid="nav-book">{t("nav.book")}</NavLink>
          {user && user.role === "admin" && (
            <NavLink to="/admin" className={link} data-testid="nav-admin">{t("nav.admin")}</NavLink>
          )}
          {user && user.role !== "admin" && (
            <NavLink to="/dashboard" className={link} data-testid="nav-dashboard">{t("nav.dashboard")}</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="lang-trigger" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/5 gap-2">
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs tracking-widest">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0A1128] border-white/10 text-white">
              <DropdownMenuItem data-testid="lang-nl" onClick={() => setLang("nl")}>Nederlands</DropdownMenuItem>
              <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")}>English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <Button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
              className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
              {t("nav.logout")}
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="hidden sm:block text-sm text-white/80 hover:text-white">
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="header-register-btn">
                <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
                  {t("nav.register")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  const s = useCompanySettings();
  return (
    <footer className="mt-32 border-t border-white/10 bg-[#040814]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl text-white">Sam</span>
            <span className="font-heading text-3xl text-gold-gradient">Taxi</span>
          </div>
          <p className="mt-3 text-white/60 text-sm leading-relaxed">
            {s.company_name} — Professional taxi & airport transfer service across the Netherlands.
          </p>
          <div className="mt-4 flex gap-3 text-white/50">
            <a href="#" data-testid="social-fb">Facebook</a>
            <a href="#" data-testid="social-ig">Instagram</a>
            <a href="#" data-testid="social-li">LinkedIn</a>
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">{t("footer.company")}</div>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/airports/schiphol">Schiphol</Link></li>
            <li><Link to="/airports/eindhoven">Eindhoven</Link></li>
            <li><Link to="/airports/rotterdam">Rotterdam The Hague</Link></li>
            <li><Link to="/airports/brussels">Brussels</Link></li>
            <li><Link to="/airports/dusseldorf">Düsseldorf</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">{t("footer.legal")}</div>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/legal/privacy" data-testid="footer-privacy">{t("footer.privacy")}</Link></li>
            <li><Link to="/legal/terms" data-testid="footer-terms">{t("footer.terms")}</Link></li>
            <li><Link to="/legal/cookies" data-testid="footer-cookies">{t("footer.cookies")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">{t("contact.title")}</div>
          <ul className="space-y-2 text-white/70 text-sm">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#D4AF37]" /> {s.phone}</li>
            <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#D4AF37]" /> {s.whatsapp}</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#D4AF37]" /> {s.email}</li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#D4AF37]" /> {s.address}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        © {new Date().getFullYear()} {s.company_name}. {t("footer.rights")}
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppFloating />
    </div>
  );
}
